import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';
import { isAdminEmail, ADMIN_CONFIG } from '../config/adminConfig';
import { adminInviteService } from '../services/adminInviteService';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // 2FA State
  const [is2FARequired, setIs2FARequired] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying2FA, setIsVerifying2FA] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Sign in with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // Check if the user is an admin
      const isUserAdmin = await adminInviteService.isEmailAdmin(userCredential.user.email || '');
      if (!isUserAdmin) {
        // Sign out the user if they're not an admin
        await auth.signOut();
        setError('Access denied. This email is not authorized for admin access.');
        return;
      }

      try {
        await adminInviteService.ensureAdminUserUidSync(userCredential.user);
      } catch (syncErr) {
        console.error('Admin UID sync failed:', syncErr);
      }

      // Check for 2FA requirement
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));

      if (userDoc.exists() && userDoc.data().adminTwoFactorEnabled) {
        setIs2FARequired(true);
        setIsLoading(false);
        return;
      }

      // Redirect to admin dashboard
      window.location.href = ADMIN_CONFIG.ADMIN_DASHBOARD_URL;
    } catch (error: any) {
      console.error('Admin login error:', error);

      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email address.');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError('Login failed. Please check your credentials and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FAVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsVerifying2FA(true);
    setError('');

    try {
      const verifyToken = httpsCallable(functions, 'verifyTwoFactorToken');
      const result = await verifyToken({ token: verificationCode });
      const data = result.data as { success: boolean, message?: string };

      if (data.success) {
        try {
          if (auth.currentUser) {
            await adminInviteService.ensureAdminUserUidSync(auth.currentUser);
          }
        } catch (syncErr) {
          console.error('Admin UID sync failed (2FA):', syncErr);
        }
        window.location.href = ADMIN_CONFIG.ADMIN_DASHBOARD_URL;
      } else {
        setError('Invalid verification code. Please try again.');
        setIsVerifying2FA(false);
      }
    } catch (err) {
      console.error('2FA verification failed:', err);
      setError('Verification failed. Please try again.');
      setIsVerifying2FA(false);
    }
  };

  // Background image URL
  const BG_IMAGE = "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop";

  return (
    <div
      className="min-h-screen relative flex flex-col font-sans text-gray-900"
      style={{
        backgroundImage: `url(${BG_IMAGE})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-black/20" />

      {/* Top Navigation Bar */}
      <nav className="relative z-10 w-full px-6 py-4 flex justify-center items-center">
        <div className="flex items-center space-x-2 px-4 py-2">
          <img
            src="/Scubamal logo for black bg.png"
            alt="Scubamail Logo"
            className="h-8 w-auto object-contain"
          />
        </div>
      </nav>

      {/* Main Content Area */}
      <div className="relative z-10 flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">

          {/* Left Column: Hero Text */}
          <div className="hidden lg:flex flex-col text-white space-y-8 p-8">
            <div>
              <h1 className="text-5xl font-bold tracking-tight mb-4">
                Manage with<br />
                confidence
              </h1>
              <p className="text-lg text-gray-200 max-w-md leading-relaxed">
                Secure access to your Scubamail administration console. Monitor campaigns, manage users, and oversee system health from one centralized dashboard.
              </p>
            </div>

            {/* Decorative boxes removed as per request */}
          </div>

          {/* Right Column: Login Card */}
          <div className="w-full max-w-md mx-auto">
            <div className="bg-white/20 backdrop-blur-xl border border-white/30 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
              {/* Card glossy reflection effect */}
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white/50 to-transparent opacity-50"></div>

              {!is2FARequired ? (
                <>
                  <div className="mb-8">
                    <h2 className="text-3xl font-bold text-white mb-2">Sign in</h2>
                    <p className="text-gray-200 text-sm">Welcome back, Admin</p>
                  </div>

                  {error && (
                    <div className="mb-6 p-4 bg-red-500/30 border border-red-500/40 rounded-xl backdrop-blur-md text-white text-sm">
                      {error}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                      {/* Custom Input Styling */}
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-5 py-4 bg-white/40 border border-white/30 rounded-xl text-gray-900 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-white/50 focus:bg-white/60 transition-all duration-200 font-medium"
                        placeholder="Enter your email address"
                      />
                    </div>

                    <div className="relative group">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full px-5 py-4 bg-white/40 border border-white/30 rounded-xl text-gray-900 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-white/50 focus:bg-white/60 transition-all duration-200 font-medium pr-12"
                        placeholder="Enter your password"
                      />
                      <button
                        type="button"
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-700 hover:text-black hover:bg-white/20 p-1.5 rounded-full transition-all duration-200"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5" />
                        ) : (
                          <EyeIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full py-4 px-6 bg-black text-white font-semibold rounded-full hover:bg-gray-900 focus:outline-none focus:ring-4 focus:ring-gray-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg transform active:scale-95"
                    >
                      {isLoading ? 'Signing in...' : 'Sign in'}
                    </button>
                  </form>

                  <div className="mt-8 relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-white/20"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 text-gray-200 bg-transparent">Protected Area</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-8 text-center">
                    <div className="mx-auto w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4 border border-white/30">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">Verification Required</h2>
                    <p className="text-gray-200 text-sm">Enter the code from your authenticator app</p>
                  </div>

                  {error && (
                    <div className="mb-6 p-4 bg-red-500/30 border border-red-500/40 rounded-xl backdrop-blur-md text-white text-sm">
                      {error}
                    </div>
                  )}

                  <form onSubmit={handle2FAVerification} className="space-y-6">
                    <input
                      id="code"
                      name="code"
                      type="text"
                      required
                      maxLength={6}
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, ''))}
                      className="w-full px-5 py-4 bg-white/40 border border-white/30 rounded-xl text-gray-900 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-white/50 focus:bg-white/60 transition-all duration-200 font-bold text-center tracking-[0.5em] text-2xl"
                      placeholder="000000"
                      autoFocus
                    />

                    <button
                      type="submit"
                      disabled={isVerifying2FA || verificationCode.length !== 6}
                      className="w-full py-4 px-6 bg-black text-white font-bold rounded-full hover:bg-gray-900 focus:outline-none focus:ring-4 focus:ring-gray-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg transform active:scale-95"
                    >
                      {isVerifying2FA ? 'Verifying...' : 'Verify Code'}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setIs2FARequired(false);
                        setVerificationCode('');
                        setError('');
                      }}
                      className="w-full text-sm text-gray-200 hover:text-white transition-colors"
                    >
                      Back to Login
                    </button>
                  </form>
                </>
              )}
            </div>

            <p className="mt-8 text-center text-xs text-gray-300/60">
              &copy; 2024 Scubamail Inc. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
